/**
 * Add Distributor Form Composer
 *
 * Simple form with just the distributor name.
 * distributorNumber is auto-generated on submission.
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  AddDistributorFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddDistributorFormData
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Truck } from 'lucide-react'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type AddDistributorComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddDistributorFormData>
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (name: keyof AddDistributorFormData, value: unknown): string | null => {
  const shape = AddDistributorFormSchema.shape[name]
  if (!shape) return null

  // Use Zod to validate the single field
  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// Composer Component
// ============================================================================

export function AddDistributorComposer(props: AddDistributorComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    return name === 'distributorName'
  }

  const upsertDistributor = useApiMutation('upsertDeviceDistributor', 'POST', {
    invalidates: ['getDeviceDistributors'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to create distributor:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddDistributorFormSchema,
    defaultValues: {
      distributorName: initialValues?.distributorName ?? '',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddDistributorFormData
      // Validate with Zod schema (single source of truth)
      const parsed = AddDistributorFormSchema.safeParse({
        distributorName: formValue.distributorName?.trim() ?? '',
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      // Transform and submit
      const apiRequest = transformToApiRequest(parsed.data)
      await upsertDistributor.mutateAsync(apiRequest)
    },
  })

  // Render a single control with explicit render function
  const renderControl = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) => validateField(control.name as keyof AddDistributorFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render form content with groups
  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []

      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  const formBody = (
    <>
      {renderFormContent()}

      <div className="pt-4 flex justify-center gap-3">
        {renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? '',
          variant: 'outline',
          onClick: onCancel,
          visibility: !!onCancel,
          type: 'button'
        })}

        {renderButtonControl({
          label: upsertDistributor.isPending
            ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Submitting...')
            : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Submit'),
          variant: 'default',
          type: 'submit'
        }, { SubmitButton: form.SubmitButton })}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Truck className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

// Re-exports
export {
  AddDistributorFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddDistributorFormData
} from './schema'
